/**
 * PronunciationEditor.jsx — "say it like this" rules, applied to every
 * paragraph before synthesis. Names, acronyms and places stop being a lottery.
 */
import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import * as L from "lucide-react";

export function PronunciationEditor({ rules = [], onChange, max = 24 }) {
  const [open, setOpen] = useState(rules.length > 0);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const add = () => {
    const f = from.trim(), t = to.trim();
    if (!f || !t || rules.length >= max) return;
    onChange([...rules, { from_text: f, to_text: t }]);
    setFrom("");
    setTo("");
  };

  const remove = (i) => onChange(rules.filter((_, idx) => idx !== i));

  return (
    <div className="rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] p-4" data-testid="studio-pronunciations">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="studio-pron-toggle"
      >
        <L.SpellCheck2 size={14} className="text-[var(--rammy-cyan-dark)]"/>
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[var(--rammy-text)]">
          Pronunciation
        </div>
        <span className="text-[11px] text-[var(--rammy-text-muted)]">
          {rules.length ? `${rules.length} rule${rules.length === 1 ? "" : "s"}` : "Optional — fix names and acronyms"}
        </span>
        <L.ChevronDown size={14} className={`ml-auto text-[var(--rammy-text-muted)] transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>

      {open && (
        <div className="mt-3 space-y-2">
          {rules.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-sm" data-testid={`studio-pron-rule-${i}`}>
              <span className="px-2 py-1 rounded-lg bg-[var(--rammy-surface-alt)] font-medium">{r.from_text}</span>
              <L.ArrowRight size={12} className="text-[var(--rammy-text-muted)]"/>
              <span className="px-2 py-1 rounded-lg bg-[var(--rammy-cyan)]/12 text-[var(--rammy-text)]">{r.to_text}</span>
              <button
                type="button"
                onClick={() => remove(i)}
                className="ml-auto text-[var(--rammy-text-muted)] hover:text-red-500 transition-colors"
                data-testid={`studio-pron-remove-${i}`}
                aria-label={`Remove rule ${i + 1}`}
              >
                <L.X size={13}/>
              </button>
            </div>
          ))}
          {rules.length < max && (
            <div className="flex items-center gap-2">
              <Input
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                placeholder="Written as"
                maxLength={60}
                className="rounded-xl h-9 text-sm flex-1 border-[var(--rammy-border)] focus-visible:border-[var(--rammy-cyan)]"
                data-testid="studio-pron-from"
              />
              <L.ArrowRight size={13} className="text-[var(--rammy-text-muted)] shrink-0"/>
              <Input
                value={to}
                onChange={(e) => setTo(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                placeholder="Said as — e.g. RAM-ee"
                maxLength={80}
                className="rounded-xl h-9 text-sm flex-1 border-[var(--rammy-border)] focus-visible:border-[var(--rammy-cyan)]"
                data-testid="studio-pron-to"
              />
              <button
                type="button"
                onClick={add}
                disabled={!from.trim() || !to.trim()}
                className="btn-pill h-9 px-3 text-[11px] bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)] border border-[var(--rammy-cyan)]/40 disabled:opacity-40 shrink-0"
                data-testid="studio-pron-add"
              >
                Add
              </button>
            </div>
          )}
          <div className="text-[10px] text-[var(--rammy-text-muted)] leading-relaxed">
            Rules apply to whole words, everywhere in the script, and never change what you see on screen.
          </div>
        </div>
      )}
    </div>
  );
}
